import React, { useState } from "react";
import {
  Paper,
  Box,
  Typography,
  TextField,
  IconButton,
  Tooltip,
  Grid,
  Button,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  Switch,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import SettingsIcon from "@mui/icons-material/Settings";
import TuneIcon from "@mui/icons-material/Tune";

function RadioButtonGroupModule({ module, onChange, onDelete, isEditable }) {
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [conditionalOpen, setConditionalOpen] = useState(false);

  // Normalize options so that each option is an object with only "label" and "value" keys.
  const normalizeOptions = (options) => {
    return (options || []).map((opt) => {
      if (typeof opt === "string") {
        return { label: opt, value: opt };
      } else if (typeof opt === "object") {
        return { label: opt.label || "", value: opt.value || "" };
      }
      return opt;
    });
  };

  // Always work with normalized options.
  const normalizedOptions = normalizeOptions(module.options);

  const handleLabelChange = (e) => {
    onChange({ ...module, label: e.target.value });
  };

  const updateOption = (index, field, value) => {
    const updatedOptions = normalizeOptions(module.options);
    updatedOptions[index] = { ...updatedOptions[index], [field]: value };
    onChange({ ...module, options: updatedOptions });
  };

  const addOption = () => {
    const updatedOptions = normalizeOptions(module.options);
    updatedOptions.push({ label: "", value: "" });
    onChange({ ...module, options: updatedOptions });
  };

  const removeOption = (index) => {
    const updatedOptions = normalizeOptions(module.options);
    updatedOptions.splice(index, 1);
    onChange({ ...module, options: updatedOptions });
  };

  const handleRequiredToggle = (e) => {
    onChange({ ...module, required: e.target.checked });
  };

  const handleAdminWorkflowToggle = (e) => {
    onChange({ ...module, approvalRequired: e.target.checked });
  };

  return (
    <Paper elevation={3} sx={{ padding: 2, marginBottom: 2 }}>
      {/* Module Header */}
      <Box sx={{ display: "flex", alignItems: "center", marginBottom: 1 }}>
        <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
          RADIO BUTTON GROUP MODULE
        </Typography>
        {isEditable && (
          <Box>
            <Tooltip title="Settings">
              <IconButton
                onClick={() => setSettingsOpen(!settingsOpen)}
                size="small"
              >
                <SettingsIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Conditional Visibility">
              <IconButton
                onClick={() => setConditionalOpen(!conditionalOpen)}
                size="small"
              >
                <TuneIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete Module">
              <IconButton onClick={onDelete} size="small" color="error">
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Box>

      {/* Module Preview */}
      <Box
        sx={{
          border: "1px solid #e0e0e0",
          borderRadius: 1,
          padding: 2,
          backgroundColor: "#fafafa",
          marginBottom: 1,
        }}
      >
        <FormControl component="fieldset">
          <Typography variant="subtitle1">
            {module.label || "Radio Button Group"}
          </Typography>
          <RadioGroup>
            {(normalizedOptions || []).map((option, index) => (
              <FormControlLabel
                key={index}
                control={<Radio disabled />}
                label={option.label}
                value={option.value}
              />
            ))}
          </RadioGroup>
        </FormControl>
      </Box>

      {/* Settings Panel */}
      {isEditable && settingsOpen && (
        <Box sx={{ marginTop: 2 }}>
          <TextField
            label="Group Label"
            value={module.label || ""}
            onChange={handleLabelChange}
            fullWidth
            margin="dense"
          />
          <Typography variant="subtitle2" sx={{ marginTop: 2 }}>
            Options:
          </Typography>
          {(normalizedOptions || []).map((option, index) => (
            <Grid container spacing={1} alignItems="center" key={index}>
              <Grid item xs={5}>
                <TextField
                  label="Label"
                  value={option.label}
                  onChange={(e) => updateOption(index, "label", e.target.value)}
                  fullWidth
                  margin="dense"
                />
              </Grid>
              <Grid item xs={5}>
                <TextField
                  label="Value"
                  value={option.value}
                  onChange={(e) => updateOption(index, "value", e.target.value)}
                  fullWidth
                  margin="dense"
                />
              </Grid>
              <Grid item xs={2}>
                <IconButton
                  onClick={() => removeOption(index)}
                  size="small"
                  color="error"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Grid>
            </Grid>
          ))}
          <Button
            variant="text"
            startIcon={<AddIcon />}
            onClick={addOption}
            sx={{ marginTop: 1 }}
          >
            Add Option
          </Button>

          {/* New Status Dropdowns */}
          <Grid container spacing={2} sx={{ marginTop: 2 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="dense">
                <InputLabel id="editable-status-label">
                  Editable when in Status:
                </InputLabel>
                <Select
                  labelId="editable-status-label"
                  value={module.editableWhenStatus || 0}
                  onChange={(e) =>
                    onChange({ ...module, editableWhenStatus: e.target.value })
                  }
                  label="Editable when in Status:"
                >
                  <MenuItem value={0}>0, valid</MenuItem>
                  <MenuItem value={8}>8, Completed</MenuItem>
                  <MenuItem value={3}>3, Awaiting Review</MenuItem>
                  <MenuItem value={2}>2, Requested</MenuItem>
                  <MenuItem value={9}>9, Closed</MenuItem>
                  <MenuItem value={7}>7, Declined</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="dense">
                <InputLabel id="visible-status-label">
                  Visible when in Status:
                </InputLabel>
                <Select
                  labelId="visible-status-label"
                  value={module.visibleWhenStatus || 0}
                  onChange={(e) =>
                    onChange({ ...module, visibleWhenStatus: e.target.value })
                  }
                  label="Visible when in Status:"
                >
                  <MenuItem value={0}>0, valid</MenuItem>
                  <MenuItem value={8}>8, Completed</MenuItem>
                  <MenuItem value={3}>3, Awaiting Review</MenuItem>
                  <MenuItem value={2}>2, Requested</MenuItem>
                  <MenuItem value={9}>9, Closed</MenuItem>
                  <MenuItem value={7}>7, Declined</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Grid container spacing={2} alignItems="center" sx={{ marginTop: 2 }}>
            <Grid item xs={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={module.required || false}
                    onChange={handleRequiredToggle}
                    color="primary"
                  />
                }
                label="Required"
              />
            </Grid>
            <Grid item xs={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={module.approvalRequired || false}
                    onChange={handleAdminWorkflowToggle}
                    color="primary"
                  />
                }
                label="Admin Workflow"
              />
            </Grid>
          </Grid>
        </Box>
      )}

      {isEditable && conditionalOpen && (
        <Box sx={{ marginTop: 2 }}>
          <Typography variant="body2">
            Conditional Visibility settings...
          </Typography>
        </Box>
      )}
    </Paper>
  );
}

export default RadioButtonGroupModule;
